import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GuestInfo, User } from '@proofroom/schema';
import { auth, share } from '../api/endpoints';
import { isUnauthorized } from '../api/client';

interface Session {
  user: User | null;
  /** Someone who unlocked a share link: set by the share screen, never by /api/auth/me, and never alongside a user */
  guest: GuestInfo | null;
  loading: boolean;
  login(email: string, password: string): Promise<User>;
  logout(): Promise<void>;
  setGuest(guest: GuestInfo | null): void;
  /** Ends the guest session on the server and forgets it here */
  leave(): Promise<void>;
  /** Folds a server answer about the signed-in user into the session without another /me round trip */
  patchUser(patch: Partial<User>): void;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [guest, setGuest] = useState<GuestInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    auth.me().then((u) => { if (alive) setUser(u); }).catch((e) => {
      if (!isUnauthorized(e)) console.warn('[session] me failed', e);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const u = await auth.login(email, password);
    setUser(u);
    return u;
  }, []);
  const logout = useCallback(async () => {
    try { await auth.logout(); } finally { setUser(null); }
  }, []);
  const leave = useCallback(async () => {
    try { await share.leave(); } finally { setGuest(null); }
  }, []);
  const patchUser = useCallback((patch: Partial<User>) => setUser((u) => (u ? { ...u, ...patch } : u)), []);

  const value = useMemo(() => ({ user, guest, loading, login, logout, setGuest, leave, patchUser }), [user, guest, loading, login, logout, leave, patchUser]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

export const canDecide = (u: User | null): boolean => !!u && (u.role === 'designer' || u.role === 'admin');
export const canEdit = (u: User | null): boolean => !!u;

/** Mirrors the server's isAgencyUser rule: a CHS agency designer or admin. */
export const isAgency = (u: User | null): boolean =>
  !!u && u.email.toLowerCase().endsWith('@chs.agency') && (u.role === 'designer' || u.role === 'admin');
